import services from "../services";
import i18n from "../i18n";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import map from "lodash/map";
import forEach from "lodash/forEach";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";

const strict = true;

const getDefaultState = () => {
  return {
    toSearch: "",
    multiSelectConfig: {
      items: [],
      activeItems: []
    },
    sortConfig: {
      list: ["A-Z", "Z-A"],
      order: "A-Z"
    },
    checkboxConfig: {
      fullList: [
        {
          label: "byClaustrophobia",
          value: "claustrophobia"
        },
        {
          label: "byObesity",
          value: "obesity"
        }
      ],
      selectedList: []
    },
    filterCriteriaResults: "",
    officeEquipment: {},
    equipmentTableKeys: [
      "numUltrasound",
      "numEchocardiograph",
      "numEcocolordoppler",
      "numEchocardiograph3D",
      "numRX",
      "numTACLayers1",
      "numTACLayers2",
      "numTACLayers3",
      "numTACLayers4",
      "numRMTesla1",
      "numRMTesla2",
      "numRMTesla3",
      "numMammography",
      "numTomosynthesis3D"
    ],
    machineryFullList: [],
    machineryList: [],
    serviceList: [],
    machineryForm: {},
    initialMachineryForm: {},
    servicesToDelete: []
  };
};

const state = getDefaultState();

const getters = {
  items: state => state.machineryList,
  toSearch: state => state.toSearch,
  multiSelectFullList: state => state.multiSelectConfig.items,
  multiSelectActiveList: state => state.multiSelectConfig.activeItems,
  orderList: state => state.sortConfig.list,
  order: state => state.sortConfig.order,
  checkboxFullList: state => state.checkboxConfig.fullList,
  checkboxSelectedList: state => state.checkboxConfig.selectedList,
  filterCriteriaResults: state => state.filterCriteriaResults,
  activeSortValue: state => `nome (${state.sortConfig.order})`,
  officeEquipment: state => state.officeEquipment,
  machineryList: state => state.machineryList,
  totalItems: state => state.machineryList.length,
  serviceList: state => state.serviceList,
  machineryForm: state => state.machineryForm,
  machineryName: state => state.machineryForm.name,
  machineryCode: state => state.machineryForm.code,
  machineryServiceList: state => state.machineryForm.serviceList,
  claustrophobia: state => state.machineryForm.managedConstraint.claustrophobia,
  obesity: state => state.machineryForm.managedConstraint.obesity,
  initialMachineryForm: state => state.initialMachineryForm,
  initialMachineryServiceList: state => state.initialMachineryForm.serviceList,
  equipmentTableKeys: state => state.equipmentTableKeys,
  getServicesToDelete: state => state.servicesToDelete
};

const mutations = {
  RESET(state) {
    // acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_TO_SEARCH(state, toSearch) {
    state.toSearch = toSearch;
  },
  SET_MULTI_SELECT_ACTIVE_LIST(state, multiSelectList) {
    state.multiSelectConfig.activeItems = multiSelectList;
  },
  SET_MULTI_SELECT_FULL_LIST(state, multiSelectFullList) {
    state.multiSelectConfig.items = map(multiSelectFullList, service => {
      return service.nameByLang;
    });
  },
  SET_ORDER(state, order) {
    state.sortConfig.order = order;
  },
  SET_CHECKBOX_SELECTED_LIST(state, selectedList) {
    state.checkboxConfig.selectedList = selectedList;
  },
  SET_FILTER_CRITERIA_RESULTS(state) {
    let criteria = [];
    if (state.toSearch) {
      criteria.push("'" + state.toSearch + "'");
    }
    if (!isEmpty(state.multiSelectConfig.activeItems)) {
      criteria.push("'" + state.multiSelectConfig.activeItems.join(", ") + "'");
    }
    if (!isEmpty(state.checkboxConfig.selectedList)) {
      let translatedList = [];
      state.checkboxConfig.selectedList.forEach(item => {
        translatedList.push(
          i18n.t(`hfManagement.machineryPerformance.${item}`)
        );
      });
      criteria.push("'" + translatedList.join(", ") + "'");
    }
    state.filterCriteriaResults = criteria.join(" & ");
  },
  SET_OFFICE_EQUIPMENT(state, officeEquipment) {
    state.officeEquipment = officeEquipment;
  },
  SET_MACHINERY_FULL_LIST(state, machineryFullList) {
    forEach(machineryFullList, machineryItem => {
      machineryItem.serviceList = orderBy(
        machineryItem.serviceList,
        service => service.nameByLang.toLowerCase(),
        "asc"
      );
    });
    state.machineryFullList = machineryFullList;
  },
  SET_MACHINERY_LIST(state, machineryList) {
    state.machineryList = machineryList;
  },
  SET_SERVICE_LIST(state, serviceList) {
    state.serviceList = serviceList;
  },
  SET_MACHINERY_FORM_NAME(state, machineryName) {
    state.machineryForm.name = machineryName;
  },
  SET_MACHINERY_FORM_CODE(state, machineryCode) {
    state.machineryForm.code = machineryCode;
  },
  SET_MACHINERY_FORM_SERVICE_LIST(state, machineryServiceList) {
    state.machineryForm.serviceList = machineryServiceList;
  },
  REMOVE_MACHINERY_FORM_SERVICE_ITEM(state, machineryServiceItem) {
    let index = findIndex(
      state.machineryForm.serviceList,
      machineryService =>
        machineryService.serviceId === machineryServiceItem.serviceId
    );
    if (index !== -1) {
      state.machineryForm.serviceList.splice(index, 1);
      state.servicesToDelete.push(machineryServiceItem.serviceId);
    }
  },
  SET_MACHINERY_FORM_CLAUSTROPHOBIA(state, claustrophobia) {
    state.machineryForm.managedConstraint.claustrophobia = claustrophobia;
  },
  SET_MACHINERY_FORM_OBESITY(state, obesity) {
    state.machineryForm.managedConstraint.obesity = obesity;
  },
  SET_SELECTED_MACHINERY_FORM(state, selectedMachineryId) {
    let machinery = find(
      state.officeEquipment.machineryList,
      machineryItem => machineryItem.machineryId === selectedMachineryId
    );
    state.machineryForm = machinery;
    state.initialMachineryForm = { ...machinery };
    state.servicesToDelete = [];
  },
  RESET_MACHINERY_FORM(state) {
    let mf = {
      name: "",
      managedConstraint: {
        obesity: false,
        claustrophobia: false
      },
      code: "",
      active: true,
      notWorkingCalendar: {
        recurrentDay: [],
        dayOfWeekConf: [],
        customDateSlot: []
      },
      serviceList: []
    };
    state.machineryForm = mf;
    state.initialMachineryForm = { ...mf };
    state.servicesToDelete = [];
  }
};

const actions = {
  async fetchOfficeEquipment({ commit, dispatch }, officeId) {
    return await services.hfManagementService
      .fetchOfficeEquipment(officeId)
      .then(officeEquipment => {
        commit("SET_OFFICE_EQUIPMENT", officeEquipment);
        commit("SET_MACHINERY_FULL_LIST", officeEquipment.machineryList);
      })
      .then(() => {
        dispatch("getItems");
      })
      .catch(error => {
        commit("error/SET_ERROR", error, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  getItems({ commit, state }) {
    return filterMachineryListPromise(
      state.machineryFullList,
      state.toSearch,
      state.multiSelectConfig.activeItems,
      state.checkboxConfig.selectedList
    )
      .then(machineryList => {
        if (!isEmpty(machineryList))
          return sortMachineryList(machineryList, state.sortConfig.order);
        else return machineryList;
      })
      .then(machineryList => {
        commit("SET_MACHINERY_LIST", machineryList);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        commit("SET_FILTER_CRITERIA_RESULTS");
        let machineryListElement = document.querySelector(
          ".machinery-list-container"
        );
        if (machineryListElement) {
          machineryListElement.scrollTo({
            top: 0,
            behavior: "auto"
          });
        }
      });
  },
  async fetchServiceList({ commit }, officeId) {
    const LOADING_DIALOG_TEXT = "dialogs.loading.contentLoadingDialogMsg";
    commit("loading/OPEN_LOADING_DIALOG", LOADING_DIALOG_TEXT, {
      root: true
    });
    return await services.hfManagementService
      .fetchHFServices(officeId)
      .then(serviceListWithAllFields => {
        let filteredServiceList = filter(serviceListWithAllFields, service => {
          return service.requireObese || service.requireClaustrophobic;
        });
        let serviceList = map(filteredServiceList, service => {
          return {
            serviceId: service.serviceId,
            nameByLang: service.label
              ? service.label.defaultValue
                ? service.label.defaultValue
                : service.label.it_IT
              : "NO_DESCRIPTION_AVAILABLE"
          };
        });
        return orderBy(
          serviceList,
          service => service.nameByLang.toLowerCase(),
          "asc"
        );
      })
      .then(serviceList => {
        commit("SET_SERVICE_LIST", serviceList);
        commit("SET_MULTI_SELECT_FULL_LIST", serviceList);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() =>
        commit("loading/CLOSE_LOADING_DIALOG", null, {
          root: true
        })
      );
  },
  createMachinery({ commit, state }, officeId) {
    return services.hfManagementService
      .createMachinery(officeId, state.machineryForm)
      .then(response => {
        return response.machineryId;
      })
      .catch(error => {
        commit("error/SET_ERROR", error, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  updateMachinery({ state }, officeId) {
    let machinery = state.machineryForm;
    let parameters = {
      id: officeId,
      machineryId: machinery.machineryId
    };
    return services.hfManagementService
      .updateMachinery(parameters, machinery)
      .then(() => {})
      .catch(error => {
        throw error;
      })
      .finally(() => {});
  },
  deleteMachinery({ commit }, machineryId) {
    return services.hfManagementService
      .deleteMachinery(machineryId)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        throw error;
      })
      .finally(() => {});
  },
  addMachineryService({ commit }, { machineryService }) {
    return services.hfManagementService
      .addMachineryService(machineryService)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        throw error;
      })
      .finally(() => {});
  },
  deleteMachineryService({ commit }, { machineryService }) {
    return services.hfManagementService
      .deleteMachineryService(machineryService)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        throw error;
      })
      .finally(() => {});
  }
};

function filterMachineryListPromise(
  machineryList,
  toSearch,
  toServiceFilter,
  toConstraintFilter
) {
  return new Promise((resolve, reject) => {
    try {
      const searchList = toSearch ? toSearch.toLowerCase().split(" ") : [];
      let filteredList = machineryList.filter(machinery => {
        let q1List = machinery.name.toLowerCase().split(" ");
        let q2List = machinery.code.toLowerCase().split(" ");
        let queries = q1List.concat(q2List);
        let newMatch =
          searchList.length == 0
            ? true
            : searchList.every(item => queries.includes(item));
        let machineryServiceList = map(machinery.serviceList, "nameByLang");
        let hasService =
          toServiceFilter.length === 0
            ? true
            : toServiceFilter.some(i => machineryServiceList.indexOf(i) >= 0);

        let machineryManagedConstraint = machinery.managedConstraint;
        let hasConstraint =
          toConstraintFilter.length === 0
            ? true
            : toConstraintFilter.some(
                i => machineryManagedConstraint[i] === true
              );
        return newMatch && hasService && hasConstraint;
      });
      resolve(filteredList);
    } catch (error) {
      console.log(error);
      reject(error);
    }
  });
}

function sortMachineryList(machineryList, order) {
  const SORT_BY = order == "A-Z" ? "asc" : "desc";
  return orderBy(
    machineryList,
    machineryItem => machineryItem.name.toLowerCase(),
    SORT_BY
  );
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
